import { DeleteCampaignInput, useDeleteCampaign } from '@hprofits/api-client';
import { SUPPRESS_ERROR_TOAST_META } from '@hprofits/api-client/error-handling';
import { MaybeRefOrGetter, Ref, toValue } from 'vue';
import { useRouter } from 'vue-router';

import { apiClient } from '@/api-client';
import { t } from '@/i18n';
import { CampaignGroupParams } from '@/modules/campaigns/composables/context/types';
import {
  getCampaignDeleteGenericErrorMessage,
  getCampaignDeleteMutationErrorOverride,
} from '@/modules/campaigns/utils/mutation-errors/campaign';
import { formatMutationError } from '@/modules/campaigns/utils/mutation-errors/shared';
import { useEntityDeleteDialog } from '@/modules/common/composables/useEntityDeleteDialog';
import { useRouteResolverCampaigns } from '@/modules/common/route/useRouteResolverCampaigns';
import { UsePageControllerResult } from '@/modules/common/state/usePageController';

// TODO: Create common type builder
type DeleteInputParams = DeleteCampaignInput &
  Readonly<{
    redirectPath: string;
  }>;

// TODO: Make common type for all deletion actions (they differ only by params type)
type CampaignDeleteProps = Readonly<{
  params: CampaignGroupParams;
  title: MaybeRefOrGetter<string | null>;
  isLoading: Readonly<Ref<boolean>>;
  pageController: UsePageControllerResult;
  allowNextNavigation: () => void;
}>;

// TODO: Make common action
type UseCampaignDeleteActionResult = Readonly<{
  handleDelete: () => Promise<void>;
  isDeletePending: Readonly<Ref<boolean>>;
}>;

export const useCampaignDeleteAction = ({
  params: { campaignGroupId },
  title,
  isLoading,
  pageController,
  allowNextNavigation,
}: CampaignDeleteProps): UseCampaignDeleteActionResult => {
  const router = useRouter();
  const routeResolver = useRouteResolverCampaigns();

  const deleteMutation = useDeleteCampaign(apiClient, SUPPRESS_ERROR_TOAST_META);

  const { isDeleting, openDeleteDialog } = useEntityDeleteDialog<DeleteInputParams>({
    resolveDisplayName: () => toValue(title) ?? `#${campaignGroupId}`,
    confirmCopy: {
      title: () => t('modules.campaigns.actions.deleteCampaignGroup'),
      description: (displayName) => t('modules.campaigns.actions.deleteCampaignDescription', { name: displayName }),
      confirmText: () => t('modules.campaigns.actions.delete'),
    },
    buildVars: () => {
      if (isLoading.value) {
        return null;
      }
      const redirectRoute = routeResolver.list();
      return { campaignId: campaignGroupId, redirectPath: redirectRoute.path };
    },
    mutate: (vars) => deleteMutation.mutateAsync({ campaignId: vars.campaignId }),
    isPending: deleteMutation.isPending,
    buildErrorMessage: (err) =>
      formatMutationError(err, getCampaignDeleteMutationErrorOverride, getCampaignDeleteGenericErrorMessage()),
    successMessage: () => t('modules.campaigns.actions.campaignDeletedToast'),
    onSuccess: async (_data, vars) => {
      allowNextNavigation();
      await router.push(vars.redirectPath);
    },
    pageController,
  });

  return {
    isDeletePending: isDeleting,
    handleDelete: openDeleteDialog,
  };
};

/*
TODO: Reimplement old logic that must prevent deleting running campaigns

const handleDeleteCampaign = async (): Promise<void> => {
  const confirmed = await confirm({
    title: 'Delete campaign',
    description: `Delete campaign "${campaignName.value}"?`,
    confirmText: 'Delete',
    confirmColor: 'red',
  });

  if (!confirmed) {
    return;
  }

  try {
    await deleteCampaign(campaignGroupId.value, false);
    toast.success(`Campaign "${campaignName.value}" deleted successfully`);
    skipNextGuard.value = true;
    router.push({ name: 'campaigns-poc-list' });
  } catch (error) {
    if (!isCampaignDeleteBlockedByDependencies(error)) {
      handleDeleteError(error);
      return;
    }

    const forceConfirmed = await confirm({
      title: 'Force delete campaign',
      description: `Campaign "${campaignName.value}" has running campaigns. Force delete anyway?`,
      confirmText: 'Force delete',
      confirmColor: 'red',
    });

    if (!forceConfirmed) {
      return;
    }

    try {
      await deleteCampaign(campaignGroupId.value, true);
      toast.success(`Campaign "${campaignName.value}" deleted successfully`);
      skipNextGuard.value = true;
      router.push({ name: 'campaigns-poc-list' });
    } catch (forceError) {
      handleDeleteError(forceError);
    }
  }
};
*/
